import React, { useEffect, useState } from 'react';
import { 
  Card, Form, Input, Button, message, 
  Typography, Space, Divider, Avatar, 
  Alert
} from 'antd';
import { 
  UserOutlined, MailOutlined, LockOutlined, 
  SaveOutlined, SafetyCertificateOutlined 
} from '@ant-design/icons';
import { usersService } from '../../services/users.service';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name,
        email: user.email,
      });
    }
  }, [user, form]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      // If password field is empty, don't send it
      const updateData = { ...values };
      delete updateData.confirmPassword;
      
      if (!updateData.password) {
        delete updateData.password;
      }

      await usersService.updateProfile(updateData);
      message.success('Cập nhật thông tin thành công');
      await refreshUser(); // Update user info in context
      form.setFieldValue('password', '');
      form.setFieldValue('confirmPassword', '');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Cập nhật thất bại');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2}>Cài đặt tài khoản</Title>
        
        <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, gap: 24 }}>
            <Avatar size={80} src={user?.profilePicture} icon={<UserOutlined />} />
            <div>
              <Title level={4} style={{ margin: 0 }}>{user?.name}</Title>
              <Text type="secondary">{user?.email || 'Chưa cập nhật email'}</Text>
              <div style={{ marginTop: 8 }}>
                {user?.isFacebookConnected ? (
                  <Alert 
                    message="Đã kết nối Facebook" 
                    type="success" 
                    showIcon 
                    icon={<SafetyCertificateOutlined />}
                    style={{ padding: '2px 12px' }}
                  />
                ) : (
                  <Alert 
                    message="Chưa kết nối Facebook" 
                    type="warning" 
                    showIcon
                    style={{ padding: '2px 12px' }}
                  />
                )}
              </div>
            </div>
          </div>

          <Divider orientation={"left" as any}>Thông tin cá nhân</Divider>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item
              name="name"
              label="Họ và tên"
              rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Nhập họ và tên" size="large" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Vui lòng nhập email' },
                { type: 'email', message: 'Email không hợp lệ' }
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="Nhập email" size="large" />
            </Form.Item>

            <Divider orientation={"left" as any}>Đổi mật khẩu</Divider>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Để trống nếu bạn không muốn thay đổi mật khẩu hiện tại.
            </Text>

            <Form.Item
              name="password"
              label="Mật khẩu mới"
              rules={[{ min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Nhập mật khẩu mới" size="large" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Xác nhận mật khẩu mới"
              dependencies={['password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Mật khẩu xác nhận không khớp!'));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Xác nhận mật khẩu mới" size="large" />
            </Form.Item>

            <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />} 
                loading={loading}
                size="large"
                block
              >
                Lưu thay đổi
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </div>
  );
};

export default Profile;
